import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BookOpen, Award, Clock, CheckCircle } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').trim();
const API = BACKEND_URL ? `${BACKEND_URL}/api` : null;

export default function StudentDashboard() {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const [enrollments, setEnrollments] = useState([]);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchEnrollments();
  }, [token]);

  const fetchEnrollments = async () => {
    try {
      if (!API) return;
      const response = await axios.get(`${API}/enrollments/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnrollments(response.data);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-8 rounded-3xl shadow-2xl mb-12">
          <h1 className="text-4xl font-extrabold font-heading mb-2" data-testid="student-dashboard-title">
            Welcome, {user.full_name}!
          </h1>
          <p className="text-xl font-hindi">आपके डिजिटल सफर में स्वागत है</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white p-6 rounded-2xl shadow-lg border-t-4 border-blue-500">
            <BookOpen className="w-12 h-12 text-blue-600 mb-4" />
            <h3 className="text-2xl font-bold mb-2">{enrollments.length}</h3>
            <p className="text-gray-600">Enrolled Courses</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-lg border-t-4 border-green-500">
            <CheckCircle className="w-12 h-12 text-green-600 mb-4" />
            <h3 className="text-2xl font-bold mb-2">
              {enrollments.filter(e => e.payment_status === 'completed').length}
            </h3>
            <p className="text-gray-600">Completed Payments</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-lg border-t-4 border-orange-500">
            <Award className="w-12 h-12 text-orange-600 mb-4" />
            <h3 className="text-2xl font-bold mb-2">0</h3>
            <p className="text-gray-600">Certificates Earned</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold font-heading mb-6">My Courses</h2>
          {enrollments.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-4 font-hindi">आपने अभी तक किसी कोर्स में नामांकन नहीं किया है</p>
              <button
                onClick={() => navigate('/training')}
                className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-3 rounded-full font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                data-testid="enroll-course-button"
              >
                Enroll in 15 Days Program
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {enrollments.map((enrollment, index) => (
                <div key={enrollment.id} className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all" data-testid={`enrollment-${index}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        15 Days Digital Success Program
                      </h3>
                      <p className="text-gray-600 mb-2">
                        <Clock className="w-4 h-4 inline mr-1" />
                        Enrolled on: {new Date(enrollment.enrolled_at).toLocaleDateString('en-IN')}
                      </p>
                    </div>
                    <span className={`px-4 py-2 rounded-full font-semibold ${
                      enrollment.payment_status === 'completed' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {enrollment.payment_status === 'completed' ? 'Active' : 'Pending Payment'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}